import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { salesRadarService } from '../../services/salesRadarService';
import { useTheme } from '../../context/ThemeContext';
import { format, parseISO, subDays } from 'date-fns';

// KPI Card Component
const KPICard = ({ title, value, change, changeLabel, icon, color, loading, onClick, isDark }) => {
  const isPositive = change > 0;
  
  const colorMap = {
    primary: { bg: '#EBF5FF', text: '#2563EB', border: '#3B82F6' },
    info: { bg: '#E0F2FE', text: '#0284C7', border: '#0EA5E9' },
    success: { bg: '#DCFCE7', text: '#16A34A', border: '#22C55E' },
    warning: { bg: '#FEF3C7', text: '#D97706', border: '#F59E0B' },
    secondary: { bg: '#F3E8FF', text: '#9333EA', border: '#A855F7' },
    error: { bg: '#FEE2E2', text: '#DC2626', border: '#EF4444' },
  };

  const colorStyle = colorMap[color] || colorMap.primary;

  return (
    <div
      onClick={onClick}
      style={{
        background: isDark ? '#1f2937' : 'white',
        borderRadius: '12px',
        padding: '1.25rem',
        border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        height: '100%',
      }}
      onMouseEnter={(e) => onClick && (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)')}
      onMouseLeave={(e) => onClick && (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)')}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
            {title}
          </div>
          {loading ? (
            <div style={{ width: '80px', height: '32px', background: isDark ? '#374151' : 'var(--gray-200)', borderRadius: '4px', animation: 'pulse 2s infinite' }} />
          ) : (
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </div>
          )}
          {change !== undefined && !loading && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.25rem', gap: '0.25rem' }}>
              <span style={{ color: isPositive ? '#16A34A' : '#DC2626', fontSize: '0.875rem' }}>
                {isPositive ? '↑' : '↓'}
              </span>
              <span style={{ color: isPositive ? '#16A34A' : '#DC2626', fontSize: '0.75rem' }}>
                {isPositive ? '+' : ''}{change}% {changeLabel || 'vs last period'}
              </span>
            </div>
          )}
        </div>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: isDark ? colorStyle.text + '30' : colorStyle.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.25rem',
        }}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// Mini Trend Chart
const MiniTrendChart = ({ data, dataKey, color, height = 60 }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>No data</span>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data}>
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          fill={color}
          fillOpacity={0.2}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Hot Leads Widget
const HotLeadsWidget = ({ leads, loading, onViewAll, isDark }) => {
  const navigate = useNavigate();

  const getScoreColor = (score) => {
    if (score >= 80) return { bg: isDark ? '#16A34A30' : '#DCFCE7', text: '#16A34A' };
    if (score >= 60) return { bg: isDark ? '#2563EB30' : '#DBEAFE', text: '#2563EB' };
    return { bg: isDark ? '#D9770630' : '#FEF3C7', text: '#D97706' };
  };

  return (
    <div className="card" style={{ height: '100%', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>🔥</span>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>Hot Leads</h3>
        </div>
        <button onClick={onViewAll} className="btn btn-link" style={{ fontSize: '0.875rem' }}>
          View All →
        </button>
      </div>
      <div>
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '0.75rem 0', borderBottom: `1px solid ${isDark ? '#374151' : 'var(--gray-100)'}` }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: isDark ? '#374151' : 'var(--gray-200)', marginRight: '0.75rem' }} />
              <div style={{ flex: 1 }}>
                <div style={{ width: '60%', height: '14px', background: isDark ? '#374151' : 'var(--gray-200)', borderRadius: '4px', marginBottom: '4px' }} />
                <div style={{ width: '40%', height: '12px', background: isDark ? '#374151' : 'var(--gray-200)', borderRadius: '4px' }} />
              </div>
            </div>
          ))
        ) : leads?.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
            No hot leads yet
          </div>
        ) : (
          leads?.slice(0, 5).map((lead) => {
            const scoreStyle = getScoreColor(lead.score);
            return (
              <div
                key={lead.id}
                onClick={() => navigate(`/sales-radar/leads/${lead.id}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 0',
                  borderBottom: `1px solid ${isDark ? '#374151' : 'var(--gray-100)'}`,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? '#374151' : 'var(--gray-50)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: isDark ? '#DC262630' : '#FEE2E2',
                  color: '#DC2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  marginRight: '0.75rem',
                }}>
                  {lead.name?.[0]?.toUpperCase() || lead.email?.[0]?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>
                    {lead.name || 'Anonymous'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
                    {lead.company || lead.email || 'Unknown'}
                  </div>
                </div>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  background: scoreStyle.bg,
                  color: scoreStyle.text,
                }}>
                  {lead.score}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Active Visitors Widget
const ActiveVisitorsWidget = ({ visitors, loading, isDark }) => {
  return (
    <div className="card" style={{ height: '100%', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#22C55E',
            animation: 'pulse 2s infinite',
          }} />
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>Active Now</h3>
        </div>
        <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
          {visitors?.length || 0} visitors online
        </div>
      </div>
      <div>
        {loading ? (
          <div style={{ height: '150px', background: isDark ? '#374151' : 'var(--gray-100)', borderRadius: '8px' }} />
        ) : visitors?.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
            No active visitors
          </div>
        ) : (
          visitors?.slice(0, 5).map((visitor, index) => (
            <div key={visitor.id || index} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0', gap: '0.75rem' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: isDark ? '#374151' : 'var(--gray-200)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
              }}>
                👤
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: '0.875rem', color: isDark ? '#f9fafb' : 'var(--gray-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {visitor.current_page || '/'}
                </div>
                <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
                  {visitor.location || 'Unknown location'}
                </div>
              </div>
              <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
                {visitor.duration || '< 1m'}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Source Distribution Widget
const SourceDistributionWidget = ({ data, loading, isDark }) => {
  const COLORS = ['#2563EB', '#16A34A', '#F59E0B', '#9333EA', '#DC2626', '#64748B'];

  const formatData = (sources) => {
    if (!sources) return [];
    return Object.entries(sources).map(([name, value], index) => ({
      name,
      value,
      color: COLORS[index % COLORS.length],
    }));
  };

  const chartData = formatData(data);

  return (
    <div className="card" style={{ height: '100%', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
      <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>
        Traffic Sources
      </h3>
      {loading ? (
        <div style={{ width: '150px', height: '150px', borderRadius: '50%', background: isDark ? '#374151' : 'var(--gray-200)', margin: '0 auto' }} />
      ) : chartData.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
          No source data
        </div>
      ) : (
        <>
          <div style={{ height: '150px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={60}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <ChartTooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: '1rem' }}>
            {chartData.map((source, index) => (
              <div
                key={index}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: source.color }} />
                  <span style={{ fontSize: '0.875rem', color: isDark ? '#d1d5db' : 'var(--gray-700)' }}>{source.name}</span>
                </div>
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>
                  {source.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Lead Trend Chart
const LeadTrendChart = ({ data, loading, period, isDark }) => {
  const periodLabel = period === '7d' ? '7 days' : period === '30d' ? '30 days' : '90 days';

  return (
    <div className="card" style={{ height: '100%', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.125rem', fontWeight: '600', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>
          Lead Activity Trend
        </h3>
        <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
          Last {periodLabel}
        </div>
      </div>
      {loading ? (
        <div style={{ height: '200px', background: isDark ? '#374151' : 'var(--gray-100)', borderRadius: '8px' }} />
      ) : !data || data.length === 0 ? (
        <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
          No trend data available
        </div>
      ) : (
        <div style={{ height: '200px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : 'var(--gray-200)'} />
              <XAxis
                dataKey="date"
                tickFormatter={(val) => format(parseISO(val), 'MM/dd')}
                fontSize={12}
                stroke={isDark ? '#9ca3af' : 'var(--gray-400)'}
              />
              <YAxis fontSize={12} stroke={isDark ? '#9ca3af' : 'var(--gray-400)'} />
              <ChartTooltip
                labelFormatter={(val) => format(parseISO(val), 'PPP')}
                contentStyle={{ borderRadius: '8px', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`, background: isDark ? '#1f2937' : 'white', color: isDark ? '#f9fafb' : 'inherit' }}
              />
              <Line type="monotone" dataKey="visitors" stroke="#9CA3AF" strokeWidth={2} dot={false} name="Visitors" />
              <Line type="monotone" dataKey="leads" stroke="#2563EB" strokeWidth={2} dot={false} name="Leads" />
              <Line type="monotone" dataKey="mqls" stroke="#16A34A" strokeWidth={2} dot={false} name="MQLs" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// Funnel Preview Widget
const FunnelPreviewWidget = ({ data, loading, navigate, isDark }) => {
  const stages = ['visitor', 'lead', 'mql', 'sql', 'opportunity', 'customer'];
  const stageColors = ['#9CA3AF', '#2563EB', '#16A34A', '#F59E0B', '#9333EA', '#DC2626'];

  return (
    <div className="card" style={{ height: '100%', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>
          Quick Funnel View
        </h3>
        <button onClick={() => navigate('/sales-radar/analytics')} className="btn btn-link" style={{ fontSize: '0.875rem' }}>
          Full View
        </button>
      </div>
      {loading ? (
        <div style={{ height: '200px', background: isDark ? '#374151' : 'var(--gray-100)', borderRadius: '8px' }} />
      ) : (
        <div>
          {stages.map((stage, index) => {
            const values = [
              data?.total_visitors || 0,
              data?.total_leads || 0,
              data?.total_mqls || 0,
              data?.total_sqls || 0,
              Math.floor((data?.total_sqls || 0) * 0.5),
              Math.floor((data?.total_sqls || 0) * 0.2),
            ];
            const maxValue = values[0] || 1;
            const value = values[index];
            const percentage = (value / maxValue) * 100;

            return (
              <div key={stage} style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', textTransform: 'capitalize' }}>
                    {stage}
                  </span>
                  <span style={{ fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>
                    {value.toLocaleString()}
                  </span>
                </div>
                <div style={{ height: '8px', background: isDark ? '#374151' : 'var(--gray-200)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${percentage}%`,
                    background: stageColors[index],
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Main Dashboard Component
const SalesRadarDashboard = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [hotLeads, setHotLeads] = useState([]);
  const [activeVisitors, setActiveVisitors] = useState([]);
  const [trendData, setTrendData] = useState([]);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [dashboard, leads, visitors, trends] = await Promise.all([
        salesRadarService.getDashboard({ period }),
        salesRadarService.getHighIntentLeads({ limit: 5 }).catch(() => []),
        salesRadarService.getActiveVisitors().catch(() => []),
        salesRadarService.getLeadTrends(period).catch(() => []),
      ]);

      setDashboardData(dashboard);
      setHotLeads(leads);
      setActiveVisitors(visitors);
      setTrendData(trends);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
      // Set mock data for development
      setDashboardData({
        total_visitors: 12543,
        total_leads: 3421,
        total_mqls: 892,
        total_sqls: 234,
        conversion_rate: 27.3,
        avg_score: 54.2,
        visitors_change: 12.5,
        leads_change: 8.3,
        mqls_change: -2.1,
        sqls_change: 15.7,
        conversion_change: 3.2,
        score_change: 5.8,
        sources: {
          'Organic': 4521,
          'Direct': 3210,
          'Referral': 2100,
          'Social': 1512,
          'Paid': 1200,
        },
      });
      setHotLeads([
        { id: 1, name: 'John Smith', company: 'Acme Corp', score: 92, email: 'john@acme.com' },
        { id: 2, name: 'Sarah Johnson', company: 'TechStart', score: 88, email: 'sarah@techstart.io' },
        { id: 3, name: 'Michael Chen', company: 'DataFlow', score: 85, email: 'mike@dataflow.com' },
        { id: 4, name: 'Emma Wilson', company: 'CloudBase', score: 82, email: 'emma@cloudbase.io' },
        { id: 5, name: 'David Brown', company: 'InnovateTech', score: 80, email: 'david@innovate.tech' },
      ]);
      // Generate mock trend data
      const mockTrend = [];
      for (let i = 29; i >= 0; i--) {
        const date = subDays(new Date(), i);
        mockTrend.push({
          date: format(date, 'yyyy-MM-dd'),
          visitors: Math.floor(300 + Math.random() * 200),
          leads: Math.floor(80 + Math.random() * 50),
          mqls: Math.floor(20 + Math.random() * 15),
        });
      }
      setTrendData(mockTrend);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [period]);

  // Refresh active visitors periodically
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const visitors = await salesRadarService.getActiveVisitors();
        setActiveVisitors(visitors);
      } catch (err) {
        // Silently fail for background refresh
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav style={{
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        color: isDark ? '#9ca3af' : 'var(--gray-600)',
        fontSize: '0.875rem'
      }}>
        <button onClick={() => navigate('/')} className="btn btn-link" style={{ padding: 0, color: 'var(--primary-600)' }}>
          Home
        </button>
        <span style={{ margin: '0 0.5rem', color: isDark ? '#6b7280' : 'var(--gray-400)' }}>/</span>
        <span style={{ color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>Sales Radar</span>
      </nav>

      {/* Header */}
      <div className="card" style={{ marginBottom: '1.5rem', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: isDark ? '#f9fafb' : 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              📡 Sales Radar Dashboard
              <span style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}>
                LIVE
              </span>
            </h2>
            <p style={{ color: isDark ? '#9ca3af' : 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Monitor and track your lead activity in real-time
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="form-select"
              style={{ minWidth: '150px' }}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <button onClick={fetchDashboardData} disabled={loading} className="btn btn-secondary">
              🔄 Refresh
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#FEF3C7',
            border: '1px solid #F59E0B',
            color: '#92400E',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
          }}>
            ⚠️ Using sample data. {error}
          </div>
        )}

        {/* KPI Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <KPICard
            title="Total Visitors"
            value={dashboardData?.total_visitors}
            change={dashboardData?.visitors_change}
            icon="👥"
            color="primary"
            loading={loading}
            isDark={isDark}
            onClick={() => navigate('/sales-radar/leads?stage=visitor')}
          />
          <KPICard
            title="Total Leads"
            value={dashboardData?.total_leads}
            change={dashboardData?.leads_change}
            icon="👤"
            color="info"
            loading={loading}
            isDark={isDark}
            onClick={() => navigate('/sales-radar/leads?stage=lead')}
          />
          <KPICard
            title="Marketing Qualified"
            value={dashboardData?.total_mqls}
            change={dashboardData?.mqls_change}
            icon="⚡"
            color="success"
            loading={loading}
            isDark={isDark}
            onClick={() => navigate('/sales-radar/leads?stage=mql')}
          />
          <KPICard
            title="Sales Qualified"
            value={dashboardData?.total_sqls}
            change={dashboardData?.sqls_change}
            icon="⭐"
            color="warning"
            loading={loading}
            isDark={isDark}
            onClick={() => navigate('/sales-radar/leads?stage=sql')}
          />
        </div>

        {/* Secondary KPI Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
        }}>
          <KPICard
            title="Conversion Rate"
            value={`${dashboardData?.conversion_rate?.toFixed(1) || 0}%`}
            change={dashboardData?.conversion_change}
            icon="📊"
            color="secondary"
            loading={loading}
            isDark={isDark}
          />
          <KPICard
            title="Average Score"
            value={Math.round(dashboardData?.avg_score || 0)}
            change={dashboardData?.score_change}
            icon="📈"
            color="error"
            loading={loading}
            isDark={isDark}
          />
          <div style={{
            background: isDark ? '#1f2937' : 'white',
            borderRadius: '12px',
            padding: '1rem',
            border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
            gridColumn: 'span 2',
          }}>
            <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
              Score Trend (30 days)
            </div>
            <MiniTrendChart data={trendData} dataKey="mqls" color="#16A34A" height={50} />
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: '1.5rem',
      }}>
        {/* Lead Trend Chart */}
        <div style={{ gridColumn: 'span 8' }}>
          <LeadTrendChart data={trendData} loading={loading} period={period} isDark={isDark} />
        </div>

        {/* Active Visitors */}
        <div style={{ gridColumn: 'span 4' }}>
          <ActiveVisitorsWidget visitors={activeVisitors} loading={loading} isDark={isDark} />
        </div>

        {/* Hot Leads */}
        <div style={{ gridColumn: 'span 4' }}>
          <HotLeadsWidget
            leads={hotLeads}
            loading={loading}
            onViewAll={() => navigate('/sales-radar/leads?score_range=hot')}
            isDark={isDark}
          />
        </div>

        {/* Source Distribution */}
        <div style={{ gridColumn: 'span 4' }}>
          <SourceDistributionWidget data={dashboardData?.sources} loading={loading} isDark={isDark} />
        </div>

        {/* Funnel Preview */}
        <div style={{ gridColumn: 'span 4' }}>
          <FunnelPreviewWidget data={dashboardData} loading={loading} navigate={navigate} isDark={isDark} />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginTop: '1.5rem', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>
          Quick Actions
        </h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/sales-radar/pipeline')} className="btn btn-primary">
            📋 View Pipeline
          </button>
          <button onClick={() => navigate('/sales-radar/scoring')} className="btn btn-secondary">
            🎯 Scoring Rules
          </button>
          <button onClick={() => navigate('/sales-radar/stages')} className="btn btn-secondary">
            📊 Stage Management
          </button>
          <button onClick={() => navigate('/sales-radar/insights')} className="btn btn-secondary">
            🤖 AI Insights
          </button>
          <button onClick={() => navigate('/sales-radar/reports')} className="btn btn-secondary">
            📄 Reports
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesRadarDashboard;
